import {cn, formatReadableDateRange} from '@/lib/utils';
import {ReactNode, useState, useEffect, useRef} from 'react';
import {Input} from './input';
import {DateInput} from './date-input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

const PROJECT_CARD_CONFIG = {
  'Project Dates': {
    defaultBg: 'bg-blue-500',
    placeholder: 'e.g., 2024-01-01 - 2024-06-30',
  },
  Compensation: {
    defaultBg: 'bg-green-500',
    placeholder: 'Enter compensation',
  },
  'Time Commitment': {
    defaultBg: 'bg-purple-500',
    placeholder: 'Enter time commitment',
  },
  Location: {
    defaultBg: 'bg-orange-500',
    placeholder: 'Enter location',
  },
} as const;

type ProjectLabels = keyof typeof PROJECT_CARD_CONFIG;

const INPUT_OVERRIDE_CLASSES =
  'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex w-full min-w-0 rounded-md border px-3 py-1 bg-gray-50 transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus:outline-none focus:ring-2 focus:ring-border focus:ring-offset-2 focus:border-inputaria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-8 text-sm outline-none';

// Validation functions
const isValidCompensationChar = (char: string): boolean => {
  // Allow: letters, numbers, $, /, space, comma, period
  return /^[a-zA-Z0-9$/\s,.]$/.test(char);
};

const isValidTimeCommitmentChar = (char: string): boolean => {
  // Allow: letters, numbers, -, /, space
  return /^[a-zA-Z0-9-/\s]$/.test(char);
};

const isValidLocationChar = (char: string): boolean => {
  // Allow: letters, numbers, space, comma, period, hyphen, apostrophe
  return /^[a-zA-Z0-9\s,.\-']$/.test(char);
};

// Filter invalid characters from string
const filterInvalidChars = (
  value: string,
  isValidChar: (char: string) => boolean,
): string => {
  return value
    .split('')
    .filter((char) => isValidChar(char))
    .join('');
};

// Component to handle text overflow with tooltip
const OverflowText = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsOverflowing(element.scrollWidth > element.clientWidth);
      }
    };

    checkOverflow();
    // Check on resize
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [children]);

  if (isOverflowing) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <p ref={textRef} className={className}>
              {children}
            </p>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs border border-border bg-popover text-popover-foreground text-xs">
            <p className="max-w-xs break-words text-xs leading-tight">
              {children}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <p ref={textRef} className={className}>
      {children}
    </p>
  );
};

const EditComponents: Record<
  ProjectLabels,
  React.FC<{
    initialValue: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
  }>
> = {
  'Project Dates': ({initialValue, onChange, disabled = false}) => {

    const formatDate = (dateString?: string) => {
      if (!dateString || dateString === 'undefined' || dateString === 'null')
        return '';

      // Handle both ISO date strings and YYYY-MM-DD format
      let date: Date;
      if (dateString.includes('T')) {
        // ISO string - parse as UTC
        date = new Date(dateString);
      } else {
        // YYYY-MM-DD format - treat as UTC date
        date = new Date(dateString + 'T00:00:00.000Z');
      }

      if (isNaN(date.getTime())) return '';

      // Format as YYYY-MM-DD for HTML date input
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const [rawStart = '', rawEnd = ''] = (initialValue ?? '')
      .split(' - ')
      .map((v) => v.trim());

    const today = new Date().toISOString().split('T')[0];
    const startDate = formatDate(rawStart);
    const endDate = formatDate(rawEnd);

    const handleDateChange = (start: string, end: string) => {
      if (onChange) {
        // Date inputs already provide YYYY-MM-DD format, so we can use them directly
        if (start && end) {
          onChange(`${start} - ${end}`);
        } else if (start) {
          onChange(start);
        } else if (end) {
          onChange(end);
        } else {
          onChange('');
        }
      }
    };

    return (
      <div className="space-y-2">
        <DateInput
          value={startDate}
          min={today}
          className={INPUT_OVERRIDE_CLASSES}
          disabled={disabled}
          onChange={(e) => {
            const newStart = e.target.value;
            handleDateChange(newStart, endDate);
          }}
        />
        <DateInput
          value={endDate}
          minFromStartDate={startDate || today}
          className={INPUT_OVERRIDE_CLASSES}
          disabled={disabled}
          onChange={(e) => {
            const newEnd = e.target.value;
            handleDateChange(startDate, newEnd);
          }}
        />
        {disabled && (
          <p className="text-xs text-muted-foreground mt-1">
            Date fields cannot be modified for a completed project.
          </p>
        )}
      </div>
    );
  },
  Compensation: ({initialValue, onChange, disabled = false}) => {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const filteredValue = filterInvalidChars(
        e.target.value,
        isValidCompensationChar,
      );
      setValue(filteredValue);
      onChange?.(filteredValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow control keys (backspace, delete, tab, etc.)
      if (
        e.ctrlKey ||
        e.metaKey ||
        [
          'Backspace',
          'Delete',
          'Tab',
          'ArrowLeft',
          'ArrowRight',
          'Home',
          'End',
        ].includes(e.key)
      ) {
        return;
      }
      // Prevent invalid characters
      if (!isValidCompensationChar(e.key)) {
        e.preventDefault();
      }
    };

    return (
      <Input
        type="text"
        value={value}
        placeholder={PROJECT_CARD_CONFIG['Compensation'].placeholder}
        className={INPUT_OVERRIDE_CLASSES}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
    );
  },
  'Time Commitment': ({initialValue, onChange, disabled = false}) => {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const filteredValue = filterInvalidChars(
        e.target.value,
        isValidTimeCommitmentChar,
      );
      setValue(filteredValue);
      onChange?.(filteredValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow control keys (backspace, delete, tab, etc.)
      if (
        e.ctrlKey ||
        e.metaKey ||
        [
          'Backspace',
          'Delete',
          'Tab',
          'ArrowLeft',
          'ArrowRight',
          'Home',
          'End',
        ].includes(e.key)
      ) {
        return;
      }
      // Prevent invalid characters
      if (!isValidTimeCommitmentChar(e.key)) {
        e.preventDefault();
      }
    };

    return (
      <Input
        type="text"
        value={value}
        placeholder={PROJECT_CARD_CONFIG['Time Commitment'].placeholder}
        className={INPUT_OVERRIDE_CLASSES}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
    );
  },
  Location: ({initialValue, onChange, disabled = false}) => {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const filteredValue = filterInvalidChars(
        e.target.value,
        isValidLocationChar,
      );
      setValue(filteredValue);
      onChange?.(filteredValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow control keys (backspace, delete, tab, etc.)
      if (
        e.ctrlKey ||
        e.metaKey ||
        [
          'Backspace',
          'Delete',
          'Tab',
          'ArrowLeft',
          'ArrowRight',
          'Home',
          'End',
        ].includes(e.key)
      ) {
        return;
      }
      // Prevent invalid characters
      if (!isValidLocationChar(e.key)) {
        e.preventDefault();
      }
    };

    return (
      <Input
        type="text"
        value={value}
        placeholder={PROJECT_CARD_CONFIG['Location'].placeholder}
        className={INPUT_OVERRIDE_CLASSES}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
    );
  },
};

interface ProjectOverviewCardEditProps {
  type: ProjectLabels;
  initialValue: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

function ProjectOverviewCardEdit({
  type,
  initialValue,
  onChange,
  disabled = false,
}: ProjectOverviewCardEditProps) {
  const Component = EditComponents[type];
  if (!Component) return null;

  return (
    <Component
      initialValue={initialValue}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

interface ProjectOverviewCardProps {
  icon: ReactNode;
  label: ProjectLabels;
  value: string | ReactNode;
  iconBgColor?: string;
  isEditable?: boolean;
  onChange?: (value: string) => void;
  disabled?: boolean;
  /** When true, appends " (Optional)" to the label */
  optional?: boolean;
}

export default function ProjectOverviewCard({
  icon,
  label,
  value,
  iconBgColor,
  isEditable = false,
  onChange,
  disabled = false,
  optional = false,
}: ProjectOverviewCardProps) {
  const defaultBg = PROJECT_CARD_CONFIG[label]?.defaultBg || 'bg-gray-500';
  const bgColorClass = iconBgColor || defaultBg;

  return (
    <div
      data-slot="card"
      className="bg-card text-card-foreground flex flex-col gap-6 rounded-xl border"
    >
      <div data-slot="card-content" className="[&amp;:last-child]:pb-6 p-4">
        <div className="flex items-start gap-3">
          <div className={cn('p-2 rounded-lg', bgColorClass)}>{icon}</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground mb-1">
              {label}
              {optional ? ' (Optional)' : ''}
            </p>
            {!isEditable ? (
              <OverflowText className="text-sm truncate overflow-hidden text-ellipsis">
                {label === 'Project Dates'
                  ? formatReadableDateRange(
                      typeof value === 'string' ? value : '',
                    )
                  : value}
              </OverflowText>
            ) : (
              <ProjectOverviewCardEdit
                type={label}
                initialValue={typeof value === 'string' ? value : ''}
                onChange={onChange}
                disabled={disabled}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
